import { Helmet } from 'react-helmet-async';
import { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { CreditCard, Building2, Shield, Lock, Loader2 } from 'lucide-react';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { useNavigate } from 'react-router-dom';
import apiService from '@/services/api';

const AddFundsPage = () => {
  const navigate = useNavigate();
  const [amount, setAmount] = useState(250);
  const [paymentMethod, setPaymentMethod] = useState('card');
  const [cardNumber, setCardNumber] = useState('');
  const [cardExpiry, setCardExpiry] = useState('');
  const [cardCvc, setCardCvc] = useState('');
  const [cardName, setCardName] = useState('');
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');

  const presetAmounts = [100, 250, 500, 1000];
  const processingFee = 0.0;
  const totalCharge = amount + processingFee;

  const handleConfirmAddFunds = async () => {
    try {
      setError('');
      setProcessing(true);

      // Validate card details for card payment
      if (paymentMethod === 'card') {
        if (!cardNumber || !cardExpiry || !cardCvc || !cardName) {
          setError('Please fill in all card details');
          setProcessing(false);
          return;
        }

        // Basic validation
        if (cardNumber.replace(/\s/g, '').length < 13) {
          setError('Invalid card number');
          setProcessing(false);
          return;
        }

        if (cardCvc.length < 3) {
          setError('Invalid CVV');
          setProcessing(false);
          return;
        }
      }

      // Process payment - in production, use Stripe
      // For now, we'll create a wallet top-up record
      const response = await apiService.request('/customer/wallet/topup', 'POST', {
        amount_pence: Math.round(totalCharge * 100),
        payment_method: paymentMethod,
        card_last_four: paymentMethod === 'card' ? cardNumber.slice(-4) : null,
      });

      if (response.success) {
        // Navigate to success page with amount
        navigate(`/wallet/funds-success?amount=${totalCharge}&method=${paymentMethod}`);
      } else {
        setError(response.error || 'Payment processing failed');
      }
    } catch (err) {
      console.error('Payment error:', err);
      setError('Failed to process payment. Please try again.');
    } finally {
      setProcessing(false);
    }
  };

  const formatCardNumber = (value: string) => {
    const v = value.replace(/\s+/g, '').replace(/[^0-9]/gi, '');
    const matches = v.match(/\d{4,16}/g);
    const match = (matches && matches[0]) || '';
    const parts = [];

    for (let i = 0, len = match.length; i < len; i += 4) {
      parts.push(match.substring(i, i + 4));
    }

    if (parts.length) {
      return parts.join(' ');
    } else {
      return value;
    }
  };

  const formatExpiry = (value: string) => {
    const v = value.replace(/\s+/g, '').replace(/[^0-9]/gi, '');
    if (v.length >= 2) {
      return v.slice(0, 2) + '/' + v.slice(2, 4);
    }
    return v;
  };

  return (
    <DashboardLayout>
      <Helmet>
        <title>Add Funds | Virewire Wallet</title>
      </Helmet>

      <div className="space-y-8">
        {/* Breadcrumb */}
        <nav className="text-sm text-gray-600">
          <button
            onClick={() => navigate('/wallet')}
            className="text-green-700 hover:underline"
          >
            Wallet
          </button>
          <span className="mx-2">•</span>
          <span>Add Funds</span>
        </nav>

        <h1 className="text-3xl font-bold text-gray-900">Add Funds to Your Wallet</h1>
        <p className="text-gray-600">
          Top up your balance to instantly book concierge services and manage property
          expenses.
        </p>

        <div className="grid grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="col-span-2 space-y-8">
            {/* Amount Section */}
            <Card className="border-1">
              <CardContent className="p-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">1. ENTER AMOUNT</h2>

                <div className="flex items-baseline gap-3 mb-8">
                  <span className="text-3xl font-bold text-gray-900">£</span>
                  <input
                    type="number"
                    value={amount}
                    onChange={(e) => setAmount(parseFloat(e.target.value) || 0)}
                    className="text-5xl font-bold text-gray-900 border-b-2 border-gray-300 pb-2 focus:border-green-600 outline-none max-w-xs"
                  />
                </div>

                <div className="flex gap-3">
                  {presetAmounts.map((preset) => (
                    <button
                      key={preset}
                      onClick={() => setAmount(preset)}
                      className={`px-4 py-2 rounded-lg font-semibold text-sm transition-colors ${
                        amount === preset
                          ? 'bg-green-700 text-white'
                          : 'bg-green-100 text-green-700 hover:bg-green-200'
                      }`}
                    >
                      +£{preset}
                    </button>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Payment Method Section */}
            <Card className="border-1">
              <CardContent className="p-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">2. PAYMENT METHOD</h2>

                <div className="space-y-3">
                  {/* Credit Card Option */}
                  <button
                    onClick={() => setPaymentMethod('card')}
                    className={`w-full p-6 border-2 rounded-lg transition-all ${
                      paymentMethod === 'card'
                        ? 'border-green-600 bg-green-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="flex items-center gap-4">
                      <CreditCard className="h-6 w-6 text-gray-600" />
                      <div className="text-left flex-1">
                        <p className="font-bold text-gray-900">Credit / Debit Card</p>
                        <p className="text-sm text-gray-600">
                          Visa, Mastercard, Amex accepted
                        </p>
                      </div>
                      {paymentMethod === 'card' && (
                        <div className="w-6 h-6 rounded-full bg-green-600 flex items-center justify-center">
                          <div className="w-2 h-2 bg-white rounded-full"></div>
                        </div>
                      )}
                    </div>
                  </button>

                  {/* Bank Transfer Option */}
                  <button
                    onClick={() => setPaymentMethod('bank')}
                    className={`w-full p-6 border-2 rounded-lg transition-all ${
                      paymentMethod === 'bank'
                        ? 'border-green-600 bg-green-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="flex items-center gap-4">
                      <Building2 className="h-6 w-6 text-gray-600" />
                      <div className="text-left flex-1">
                        <p className="font-bold text-gray-900">Bank Transfer</p>
                        <p className="text-sm text-gray-600">
                          Transfer funds from your corporate account
                        </p>
                      </div>
                      {paymentMethod === 'bank' && (
                        <div className="w-6 h-6 rounded-full bg-green-600 flex items-center justify-center">
                          <div className="w-2 h-2 bg-white rounded-full"></div>
                        </div>
                      )}
                    </div>
                  </button>
                </div>

                {/* Card Details Form */}
                {paymentMethod === 'card' && (
                  <div className="mt-6 p-6 bg-gray-50 rounded-lg border border-gray-200 space-y-4">
                    <h3 className="font-semibold text-gray-900 mb-4">Card Details</h3>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Cardholder Name</label>
                      <input
                        type="text"
                        value={cardName}
                        onChange={(e) => setCardName(e.target.value)}
                        placeholder="John Doe"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-600 focus:border-transparent outline-none"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Card Number</label>
                      <input
                        type="text"
                        value={cardNumber}
                        onChange={(e) => setCardNumber(formatCardNumber(e.target.value))}
                        placeholder="4242 4242 4242 4242"
                        maxLength="19"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-600 focus:border-transparent outline-none"
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Expiry (MM/YY)</label>
                        <input
                          type="text"
                          value={cardExpiry}
                          onChange={(e) => setCardExpiry(formatExpiry(e.target.value))}
                          placeholder="MM/YY"
                          maxLength="5"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-600 focus:border-transparent outline-none"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">CVV</label>
                        <input
                          type="text"
                          value={cardCvc}
                          onChange={(e) => setCardCvc(e.target.value.replace(/[^0-9]/g, '').slice(0, 4))}
                          placeholder="123"
                          maxLength="4"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-600 focus:border-transparent outline-none"
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* Bank Transfer Info */}
                {paymentMethod === 'bank' && (
                  <div className="mt-6 p-6 bg-blue-50 rounded-lg border border-blue-200 space-y-3">
                    <h3 className="font-semibold text-gray-900">Bank Transfer Details</h3>
                    <p className="text-sm text-gray-700">
                      Once you confirm, we'll provide you with our bank details. Transfer the amount within 24 hours to complete your top-up.
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Error Alert */}
            {error && (
              <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-800">{error}</p>
              </div>
            )}

            {/* Transaction Summary */}
            <Card className="border-1">
              <CardContent className="p-6">
                <h3 className="font-bold text-gray-900 mb-6">Transaction Summary</h3>

                <div className="space-y-4 mb-6 pb-6 border-b border-gray-200">
                  <div className="flex items-center justify-between">
                    <p className="text-gray-600">Amount</p>
                    <p className="font-semibold text-gray-900">£{amount.toFixed(2)}</p>
                  </div>
                  <div className="flex items-center justify-between">
                    <p className="text-gray-600">Processing Fee</p>
                    <p className="font-semibold text-gray-900">£{processingFee.toFixed(2)}</p>
                  </div>
                </div>

                <div className="flex items-center justify-between mb-6">
                  <p className="font-bold text-gray-900">Total Charge</p>
                  <p className="text-2xl font-bold text-gray-900">
                    £{totalCharge.toFixed(2)}
                  </p>
                </div>

                <Button
                  onClick={handleConfirmAddFunds}
                  disabled={processing}
                  className="w-full bg-green-700 hover:bg-green-800 text-white font-semibold py-3 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {processing ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    'Confirm & Add Funds'
                  )}
                </Button>

                <p className="text-xs text-gray-600 mt-4 text-center">
                  By clicking confirm, you authorise a charge to your selected payment
                  method. We manage your funds securely in accordance with our financial
                  standards.
                </p>
              </CardContent>
            </Card>

            {/* Security Info */}
            <div className="flex gap-4 text-sm">
              <div className="flex items-center gap-2 text-green-700 font-semibold">
                <Shield className="h-4 w-4" />
                SSL SECURED
              </div>
              <div className="flex items-center gap-2 text-green-700 font-semibold">
                <Lock className="h-4 w-4" />
                PCI COMPLIANT
              </div>
            </div>

            {/* Info Box */}
            <Card className="border-1 bg-blue-50 border-blue-200">
              <CardContent className="p-6">
                <p className="text-sm text-gray-700">
                  <span className="font-semibold">💡 Need a larger amount?</span>
                  <br />
                  For corporate account funding over £10,000, please contact our
                  relationship management team for prioritized bank wires.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default AddFundsPage;
